import {
  listlinkedentitiesforobjects,
  ListlinkedentitiesforobjectsData,
  ListlinkedentitiesforobjectsResponse,
  listlinksforthegiventargetentity,
  ListlinksforthegiventargetentityData,
  ListlinksforthegiventargetentityResponse,
  createobjectlink,
  CreateobjectlinkData,
  CreateobjectlinkResponse,
  deleteaobjectlink,
  DeleteaobjectlinkData,
  DeleteaobjectlinkResponse,
} from '@data-access/connect/tc-tcps';
import type { Client } from '@data-access/connect/tc-issue-management';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';

/**
 * Input shape for {@link ObjectLinkApiService.listLinksForTargetEntityQuery}.
 *
 * Reshapes the generated query type to match TCPS's actual contract for
 * `/objectlink/target`:
 *   - `contextId` is required for `type === 'ISSUE'` (not in generated types).
 *   - `versionId` and `includeDeleted` are optional (the backend accepts the
 *     request without them; the legacy `tcweb` client never sends them).
 *
 * Remove this shim once the generated types in `@data-access/connect/tc-tcps`
 * are updated.
 */
export type ListLinksForTargetEntityQueryData = {
  query: Omit<
    ListlinksforthegiventargetentityData['query'],
    'versionId' | 'includeDeleted'
  > & {
    contextId?: string;
    versionId?: string;
    includeDeleted?: boolean;
  };
};

export class ObjectLinkApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  // Query for listing linked entities for objects
  public listLinkedEntitiesForObjectsQuery(
    data: Accessor<ListlinkedentitiesforobjectsData | undefined>
  ): UseQueryResult<ListlinkedentitiesforobjectsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['object-links', data()],
      queryFn: async (): Promise<ListlinkedentitiesforobjectsResponse> => {
        const queryData = data();

        const response = await listlinkedentitiesforobjects({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          query: queryData?.query,
        });

        if (response.error) {
          throw new Error('Failed to list linked entities for objects');
        }

        return response.data!;
      },
      enabled: !!data(),
    }));
  }

  // Query for listing links for the given target entity (e.g. an ISSUE).
  // The backend requires `contextId` when `type === 'ISSUE'`, which is not
  // yet reflected in the generated types — so we extend the query shape
  // locally and forward it on the outbound request.
  public listLinksForTargetEntityQuery(
    data: Accessor<ListLinksForTargetEntityQueryData | undefined>
  ): UseQueryResult<ListlinksforthegiventargetentityResponse, Error> {
    return useQuery(() => ({
      queryKey: ['object-links', 'target', data()],
      queryFn: async (): Promise<ListlinksforthegiventargetentityResponse> => {
        const queryData = data();
        // `enabled: !!data()` gates the query, but the accessor can return
        // `undefined` here if the upstream signal changed between the enabled
        // check and this callback — guard explicitly to avoid a non-null
        // assertion on a possibly-undefined value.
        if (!queryData) {
          throw new Error(
            'listLinksForTargetEntityQuery invoked with undefined data'
          );
        }

        const response = await listlinksforthegiventargetentity({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- client type mismatch between packages
          client: this.client as any,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- generated types omit contextId; cast to forward it on the outbound request
          query: queryData.query as any,
        });

        if (response.error) {
          throw new Error('Failed to list links for target entity');
        }

        return response.data!;
      },
      enabled: !!data(),
    }));
  }

  // Mutation for creating a new object link
  public createObjectLinkMutation(): UseMutationResult<
    CreateobjectlinkResponse,
    Error,
    CreateobjectlinkData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: CreateobjectlinkData
      ): Promise<CreateobjectlinkResponse> => {
        const response = await createobjectlink({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to create object link');
        }

        return response.data!;
      },
      onSuccess: () => {
        // Invalidate object links queries
        void this.queryClient.invalidateQueries({
          queryKey: ['object-links'],
        });
      },
    }));
  }

  // Mutation for deleting an object link
  public deleteObjectLinkMutation(): UseMutationResult<
    DeleteaobjectlinkResponse,
    Error,
    DeleteaobjectlinkData
  > {
    return useMutation(() => ({
      mutationFn: async (
        data: DeleteaobjectlinkData
      ): Promise<DeleteaobjectlinkResponse> => {
        const response = await deleteaobjectlink({
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          client: this.client as any,
          ...data,
        });

        if (response.error) {
          throw new Error('Failed to delete object link');
        }

        return response.data!;
      },
      onSuccess: (_, variables) => {
        // Invalidate object links queries
        void this.queryClient.invalidateQueries({
          queryKey: ['object-links'],
        });

        // Remove specific object link from cache if needed
        this.queryClient.removeQueries({
          queryKey: ['object-links', variables],
        });
      },
    }));
  }
}
